/**
 * v0.16.3 (B4) — fila de gravações do Modo Corrida: guarda no aparelho e sincroniza com o servidor.
 * Sem DOM e sem rede própria: o armazenamento (`GpsStore`), o envio (`Post`) e o relógio entram por parâmetro, então
 * tudo é testável com um armazenamento em memória. O IndexedDB de verdade está em `gpsStore.ts`.
 *
 * Regra de ouro: **nunca apagar a única cópia antes de o servidor confirmar** (201/200 com o mesmo nº de pontos).
 * Só tipos são importados de `gps.ts` (o `node --test` não resolve imports sem extensão).
 */

import type { Ev, GpsRecordingBody } from "./gps";

export type Phase = "recording" | "finalizing" | "pending_sync" | "synced" | "rejected";

export interface Session {
  id: string;                 // = client_id enviado ao servidor
  userId: string;
  orgId: string;
  phase: Phase;
  createdAt: number;
  updatedAt: number;
  lastEventAt: number;
  evCount: number;
  distanceM: number;          // resumo para a lista (não é coordenada)
  activeS: number;
  body: GpsRecordingBody | null;   // só entre confirmar e a confirmação do servidor
  tries: number;
  nextTryAt: number;
  lastError: string | null;   // código/mensagem sem coordenadas
  activityId: string | null;
  syncedAt: number | null;
}

export interface GpsStore {
  putSession(s: Session): Promise<void>;
  getSession(id: string): Promise<Session | undefined>;
  listSessions(userId: string): Promise<Session[]>;
  appendEvents(id: string, fromSeq: number, evs: Ev[]): Promise<void>;
  getEvents(id: string): Promise<Ev[]>;
  deleteEvents(id: string): Promise<void>;
  deleteSession(id: string): Promise<void>;   // sessão e eventos
}

export interface PostResult { status: number; code?: string; message?: string; data?: { activity_id?: string; points_kept?: number } }
export type Post = (orgId: string, body: GpsRecordingBody) => Promise<PostResult>;

export const LIMITS = {
  maxEvents: 50_000,            // ≈ 14 h a 1 leitura/s
  maxUnsynced: 20,              // gravações ainda não confirmadas pelo servidor
  tombstoneDays: 7,             // registro mínimo (sem coordenadas) depois de sincronizar
  serverMaxAgeDays: 30,         // o servidor recusa gravações mais antigas (B2)
  flushMs: 3000,
} as const;
const DAY = 86_400_000;
const BACKOFF = [30_000, 120_000, 600_000, 3_600_000];
export const backoffMs = (tries: number): number => BACKOFF[Math.min(Math.max(tries - 1, 0), BACKOFF.length - 1)];

export const isUnsynced = (s: Session): boolean => s.phase !== "synced";
export const canStart = (sessions: Session[]): boolean => sessions.filter(isUnsynced).length < LIMITS.maxUnsynced;
/** Gravações com mais de 30 dias o servidor recusa; avisar o usuário em vez de tentar para sempre. */
export const isExpired = (s: Session, now: number): boolean => s.phase === "pending_sync" && now - s.createdAt > LIMITS.serverMaxAgeDays * DAY;

// ----------------------------------------------------------------------------- gravação incremental

/** Eventos que forçam gravação imediata no aparelho (o resto espera o lote de ~3 s). */
export const FLUSH_NOW: Ev["type"][] = ["pause", "resume", "finish", "confirm", "denied"];

export class Persister {
  private store: GpsStore;
  private s: Session;
  private buf: Ev[] = [];
  private seq: number;
  private lastAt: number;
  private chain: Promise<void> = Promise.resolve();
  full = false;

  constructor(store: GpsStore, s: Session) { this.store = store; this.s = s; this.seq = s.evCount; this.lastAt = s.lastEventAt; }

  get id(): string { return this.s.id; }

  /** Enfileira o evento. Devolve false se a gravação passou do limite de eventos (não grava mais). */
  push(ev: Ev, at: number): boolean {
    if (this.seq + this.buf.length >= LIMITS.maxEvents) { this.full = true; return false; }
    this.buf.push(ev);
    this.lastAt = Math.max(this.lastAt, at);
    return true;
  }

  /**
   * Grava o que está no buffer. Chamadas são serializadas. Se a escrita falhar (armazenamento cheio ou bloqueado), os
   * eventos voltam ao início do buffer e a sequência não avança: a próxima chamada tenta de novo, sem perder nem
   * duplicar nada (gravar a mesma chave `[id, seq]` duas vezes é inofensivo).
   */
  flush(snapshot: { distanceM: number; activeS: number }, now: number): Promise<void> {
    const run = async () => {
      const evs = this.buf; this.buf = [];
      const from = this.seq;
      try {
        if (evs.length) await this.store.appendEvents(this.s.id, from, evs);
        const next: Session = { ...this.s, updatedAt: now, lastEventAt: this.lastAt, evCount: from + evs.length, ...snapshot };
        await this.store.putSession(next);
        this.s = next; this.seq = from + evs.length;
      } catch (e) {
        this.buf = [...evs, ...this.buf];
        throw e;
      }
    };
    this.chain = this.chain.catch(() => undefined).then(run);
    return this.chain;
  }

  get pending(): number { return this.buf.length; }
  get session(): Session { return this.s; }
}

export function newSession(id: string, userId: string, orgId: string, now: number): Session {
  return { id, userId, orgId, phase: "recording", createdAt: now, updatedAt: now, lastEventAt: now, evCount: 0, distanceM: 0, activeS: 0,
    body: null, tries: 0, nextTryAt: 0, lastError: null, activityId: null, syncedAt: null };
}

/** Confirmar: grava o corpo localmente ANTES de qualquer rede. A partir daqui a gravação está segura no aparelho. */
export async function confirmSession(store: GpsStore, s: Session, body: GpsRecordingBody, now: number): Promise<Session> {
  const next: Session = { ...s, phase: "pending_sync", body, updatedAt: now, tries: 0, nextTryAt: 0, lastError: null };
  await store.putSession(next);
  return next;
}

// ----------------------------------------------------------------------------- sincronização

type Outcome = "ok" | "removed" | "retry" | "rejected";

export function classify(r: PostResult): Outcome {
  if (r.status === 200 || r.status === 201) return "ok";
  if (r.status === 409 && r.code === "gps_recording_deleted") return "removed";
  if (r.status === 0 || r.status === 408 || r.status === 429 || r.status === 401 || r.status === 403 || r.status >= 500) return "retry";
  return "rejected";   // 400, 404, 413, 422...: reenviar o mesmo corpo não resolve
}

const inflight = new Set<string>();

async function tombstone(store: GpsStore, s: Session, now: number, activityId: string | null): Promise<Session> {
  const t: Session = { ...s, phase: "synced", body: null, evCount: 0, activityId, syncedAt: now, updatedAt: now, lastError: null, tries: 0 };
  await store.putSession(t);          // 1º o registro novo...
  await store.deleteEvents(s.id).catch(() => undefined);   // ...2º apaga a rota (se falhar, `purge` termina o serviço)
  return t;
}

/** Tenta enviar uma gravação pendente. Falha de rede/servidor não lança: o resultado fica na própria sessão. */
export async function syncSession(store: GpsStore, s: Session, post: Post, now: number): Promise<Session> {
  if (s.phase !== "pending_sync" || !s.body || inflight.has(s.id)) return s;
  inflight.add(s.id);
  try {
    let res: PostResult;
    try { res = await post(s.orgId, s.body); } catch { res = { status: 0 }; }
    const out = classify(res);
    if (out === "ok") {
      const same = !!res.data?.activity_id && res.data.points_kept === s.body.points.length;
      if (same) return await tombstone(store, s, now, res.data!.activity_id!);
      // Resposta que não prova a consolidação: mantém a cópia e tenta de novo.
      return await fail(store, s, now, "verification_failed");
    }
    if (out === "removed") return await tombstone(store, s, now, null);
    if (out === "rejected") {
      const r: Session = { ...s, phase: "rejected", lastError: res.message || res.code || `http_${res.status}`, updatedAt: now };
      await store.putSession(r);
      return r;
    }
    return await fail(store, s, now, res.code || `http_${res.status}`);
  } finally { inflight.delete(s.id); }
}

async function fail(store: GpsStore, s: Session, now: number, error: string): Promise<Session> {
  const tries = s.tries + 1;
  const r: Session = { ...s, tries, nextTryAt: now + backoffMs(tries), lastError: error, updatedAt: now };
  await store.putSession(r);
  return r;
}

/** Envia o que está vencido (ou tudo, com `force`). Sequencial, para não disputar a rede. */
export async function syncAll(store: GpsStore, userId: string, post: Post, now: number, force = false): Promise<Session[]> {
  const all = await store.listSessions(userId);
  for (const s of all) {
    if (s.phase === "pending_sync" && (force || s.nextTryAt <= now) && !isExpired(s, now)) await syncSession(store, s, post, now);
  }
  return store.listSessions(userId);
}

/** Limpeza segura: nunca toca em gravação não sincronizada. */
export async function purge(store: GpsStore, userId: string, now: number): Promise<void> {
  for (const s of await store.listSessions(userId)) {
    if (s.phase !== "synced") continue;
    await store.deleteEvents(s.id);   // sobra de uma limpeza interrompida
    if (s.syncedAt !== null && now - s.syncedAt > LIMITS.tombstoneDays * DAY) await store.deleteSession(s.id);
  }
}

/** Gravações que ficaram pela metade (página caiu): o usuário decide o que fazer. */
export const interrupted = (sessions: Session[]): Session[] => sessions.filter((s) => s.phase === "recording" || s.phase === "finalizing");
